import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Sparkles, RefreshCcw, Clock, TrendingUp, TrendingDown } from "lucide-react";
import client, { fmtPct, fmtUsd } from "../lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  return `${Math.round(diffMin / 60)}h ago`;
};

const MoverRow = ({ c, kind }) => {
  const positive = kind === "gainer";
  return (
    <Link
      to={`/asset/crypto/${c.id}`}
      data-testid={`overview-${kind}-${c.symbol}`}
      className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.04] transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <img src={c.image} alt={c.name} className="w-6 h-6 rounded-full" />
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{c.name}</div>
          <div className="text-[10px] uppercase text-neutral-500">{c.symbol}</div>
        </div>
      </div>
      <div className={`inline-flex items-center gap-1 text-xs font-mono-num ${positive ? "up" : "down"}`}>
        {positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
        {fmtPct(c.change)}
      </div>
    </Link>
  );
};

const MarketsAIOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const { data } = await client.get(`/ai/markets-overview${force ? "?force=true" : ""}`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const stats = data?.stats;

  return (
    <section className="glass-strong p-6 lg:p-8 relative overflow-hidden" data-testid="markets-ai-overview">
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{ backgroundImage: "radial-gradient(circle at 90% 0%, rgba(59,130,246,0.30), transparent 55%), radial-gradient(circle at 0% 100%, rgba(16,185,129,0.18), transparent 55%)" }}
      />
      <div className="relative">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/15 flex items-center justify-center flex-shrink-0">
              <Sparkles size={16} className="text-blue-400" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-400">AI Markets Overview</div>
              <div className="font-display text-base mt-1 text-neutral-200">Powered by Claude · refreshed hourly</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {data?.generated_at && (
              <div className="hidden md:flex items-center gap-1.5 text-xs text-neutral-400">
                <Clock size={11} />
                <span>{timeAgo(data.generated_at)}</span>
              </div>
            )}
            <button
              data-testid="markets-ai-refresh"
              onClick={() => load(true)}
              disabled={refreshing}
              className="btn-glass !py-2 !px-3 text-xs flex items-center gap-2"
            >
              <RefreshCcw size={12} className={refreshing ? "animate-spin" : ""} />
              <span>{refreshing ? "Updating…" : "Refresh"}</span>
            </button>
          </div>
        </div>

        {/* Top stat strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Market Cap</div>
              <div className="font-mono-num text-lg mt-1">{fmtUsd(stats.total_market_cap_usd, { compact: true, digits: 2 })}</div>
              <div className={`text-xs font-mono-num ${stats.market_cap_change_24h >= 0 ? "up" : "down"}`}>{fmtPct(stats.market_cap_change_24h)}</div>
            </div>
            <div className="px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">24h Volume</div>
              <div className="font-mono-num text-lg mt-1">{fmtUsd(stats.total_volume_usd, { compact: true, digits: 2 })}</div>
            </div>
            <div className="px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">BTC Dominance</div>
              <div className="font-mono-num text-lg mt-1">{stats.btc_dominance?.toFixed(2)}%</div>
            </div>
            <div className="px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">ETH Dominance</div>
              <div className="font-mono-num text-lg mt-1">{stats.eth_dominance?.toFixed(2)}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Markdown commentary */}
          <div className="lg:col-span-2 min-w-0">
            {loading && (
              <div className="space-y-3" data-testid="overview-loading">
                <div className="h-6 w-1/3 bg-white/5 rounded animate-pulse" />
                <div className="h-4 w-full bg-white/5 rounded animate-pulse" />
                <div className="h-4 w-5/6 bg-white/5 rounded animate-pulse" />
                <div className="h-4 w-4/6 bg-white/5 rounded animate-pulse" />
                <div className="text-xs text-neutral-500 pt-2">Claude is reading the tape…</div>
              </div>
            )}
            {error && !loading && (
              <div className="text-rose-400 text-sm" data-testid="overview-error">
                Couldn't load overview: {error}.{" "}
                <button onClick={() => load(true)} className="underline">Try again</button>
              </div>
            )}
            {!loading && !error && data?.content && (
              <div className="markets-md text-sm">
                <ReactMarkdown
                  components={{
                    h2: ({ node, ...p }) => <h3 className="font-display text-sm uppercase tracking-widest text-neutral-400 mt-5 first:mt-0 mb-2" {...p} />,
                    p: ({ node, ...p }) => <p className="text-neutral-200 leading-relaxed mb-3" {...p} />,
                    ul: ({ node, ...p }) => <ul className="space-y-1.5 mb-4 list-disc pl-5 text-neutral-200" {...p} />,
                    li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
                    strong: ({ node, ...p }) => <strong className="text-white font-semibold" {...p} />,
                    em: ({ node, ...p }) => <em className="text-neutral-500 not-italic block mt-4 text-xs" {...p} />,
                  }}
                >
                  {data.content}
                </ReactMarkdown>
              </div>
            )}
          </div>

          {/* Gainers / Losers side panel */}
          <div className="space-y-4">
            {data?.gainers?.length > 0 && (
              <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4" data-testid="overview-gainers">
                <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-emerald-400">
                  <TrendingUp size={11} /> Top Gainers
                </div>
                <div className="space-y-1">
                  {data.gainers.map((c) => <MoverRow key={c.id} c={c} kind="gainer" />)}
                </div>
              </div>
            )}
            {data?.losers?.length > 0 && (
              <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4" data-testid="overview-losers">
                <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-rose-400">
                  <TrendingDown size={11} /> Top Losers
                </div>
                <div className="space-y-1">
                  {data.losers.map((c) => <MoverRow key={c.id} c={c} kind="loser" />)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketsAIOverview;
